<script>
/**
 * BaileysConnectionStatus.vue
 * Componente que mostra o status de conexao do WhatsApp Baileys no painel de contato
 * e permite reconectar atraves de um modal
 */
import { mapGetters } from 'vuex';
import InboxesAPI from 'dashboard/api/inboxes';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BaileysConnectionModal from './BaileysConnectionModal.vue';

export default {
  name: 'BaileysConnectionStatus',
  components: {
    Dialog,
    BaileysConnectionModal,
  },
  props: {
    inboxId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      // Iniciar como true para mostrar "Verificando..." até ter dados reais
      isCheckingStatus: true,
    };
  },
  computed: {
    ...mapGetters({
      getInbox: 'inboxes/getInbox',
      getBaileysConnectionStatus: 'inboxes/getBaileysConnectionStatus',
    }),
    inbox() {
      return this.getInbox(this.inboxId);
    },
    connectionStatus() {
      return this.getBaileysConnectionStatus(this.inboxId);
    },
    isBaileysChannel() {
      return (
        this.inbox?.channel_type === 'Channel::Whatsapp' &&
        this.inbox?.provider === 'baileys'
      );
    },
    isConnected() {
      const status = (this.connectionStatus?.status || '').toUpperCase();
      return status === 'OPEN' || status === 'CONNECTED';
    },
    statusConfig() {
      if (this.isCheckingStatus) {
        return {
          label: 'Verificando...',
          color: 'bg-n-slate-9',
          icon: 'i-lucide-loader-2',
          iconClass: 'animate-spin',
          theme: 'neutral', // Tema neutro durante verificacao
        };
      }

      if (this.isConnected) {
        return {
          label: 'WhatsApp Conectado',
          color: 'bg-n-teal-9',
          icon: 'i-lucide-wifi',
          iconClass: '',
          theme: 'success',
        };
      }

      return {
        label: 'WhatsApp Desconectado',
        color: 'bg-n-ruby-9',
        icon: 'i-lucide-wifi-off',
        iconClass: '',
        theme: 'error',
      };
    },
    buttonClasses() {
      if (this.isCheckingStatus) {
        return 'bg-n-slate-3 hover:bg-n-slate-4 border border-n-slate-6';
      }
      if (this.isConnected) {
        return 'bg-n-teal-3 hover:bg-n-teal-4 border border-n-teal-6';
      }
      return 'bg-n-ruby-3 hover:bg-n-ruby-4 border border-n-ruby-6';
    },
    textClasses() {
      if (this.isCheckingStatus) {
        return 'text-n-slate-11';
      }
      return this.isConnected ? 'text-n-teal-11' : 'text-n-ruby-11';
    },
    arrowClasses() {
      if (this.isCheckingStatus) {
        return 'text-n-slate-10';
      }
      return this.isConnected ? 'text-n-teal-10' : 'text-n-ruby-10';
    },
  },
  watch: {
    inboxId: {
      handler(newId) {
        if (newId && this.isBaileysChannel) {
          this.checkStatus();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.isBaileysChannel) {
      this.checkStatus();
    }
  },
  methods: {
    async checkStatus() {
      if (!this.inboxId || !this.isBaileysChannel) return;

      this.isCheckingStatus = true;

      try {
        const response = await InboxesAPI.baileysGetStatus(this.inboxId);

        if (response.data?.success) {
          this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
            inboxId: this.inboxId,
            status: response.data.data,
          });
        } else {
          this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
            inboxId: this.inboxId,
            status: { status: 'error' },
          });
        }
      } catch (error) {
        const status = error.response?.status === 404
          ? { status: 'not_found' }
          : { status: 'error' };
        this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
          inboxId: this.inboxId,
          status,
        });
      } finally {
        this.isCheckingStatus = false;
      }
    },

    openConnectionModal() {
      this.$refs.connectionDialog?.open();
    },

    closeConnectionModal() {
      this.$refs.connectionDialog?.close();
    },
  },
};
</script>

<template>
  <div v-if="isBaileysChannel" class="w-full">
    <!-- Status Button -->
    <button
      v-tooltip.top="statusConfig.label"
      class="flex items-center gap-2 w-full px-3 py-2 rounded-lg transition-colors"
      :class="buttonClasses"
      @click="openConnectionModal"
    >
      <!-- Status Indicator -->
      <span
        class="w-2.5 h-2.5 rounded-full flex-shrink-0"
        :class="[statusConfig.color, { 'animate-pulse': !isConnected && !isCheckingStatus }]"
      />

      <!-- Icon -->
      <span
        class="text-base flex-shrink-0"
        :class="[statusConfig.icon, statusConfig.iconClass, textClasses]"
      />

      <!-- Label -->
      <span
        class="text-sm font-medium truncate"
        :class="textClasses"
      >
        {{ statusConfig.label }}
      </span>

      <!-- Arrow -->
      <span
        class="i-lucide-chevron-right text-sm ml-auto flex-shrink-0"
        :class="arrowClasses"
      />
    </button>

    <!-- Connection Modal -->
    <Dialog
      ref="connectionDialog"
      title="Conexao WhatsApp"
      width="lg"
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <BaileysConnectionModal
        v-if="inbox"
        :inbox="inbox"
      />
    </Dialog>
  </div>
</template>
